import { createFeatureSelector, createSelector } from '@ngrx/store';

import { State, errorFeatureKey } from './error.reducer';
import { fromCore } from '@core/store';
import { Error, ErrorTemplate, ErrorTemplateCount } from '../models/error.model';
import { DateTime } from 'luxon';
import { StringHelper } from '@core/helpers/string.helper';

const selectErrorState = createFeatureSelector<State>(errorFeatureKey);

// Raw selectors

export const getError = createSelector(selectErrorState, (state: State) => state.error);

export const getErrors = createSelector(selectErrorState, (state: State) => state.errors);

export const getErrorConfig = createSelector(selectErrorState, (state: State) => state.errorConfig);

export const getErrorDisplay = createSelector(selectErrorState, (state: State) => state.errorDisplay);

export const getTemplateSearch = createSelector(selectErrorState, (state: State) => state.errorSearch);

// Calculated selectors

export const getBuiltErrors = createSelector(
  fromCore.getErrorTemplates,
  getErrors,
  getTemplateSearch,
  (templates: Array<ErrorTemplate>, errors: Array<Error>, search: string) => {
    const normalized = StringHelper.normalize(search);
    const filtered = search
      ? templates.filter(
          template =>
            StringHelper.normalize(template.title).includes(normalized) ||
            StringHelper.normalize(template.description).includes(normalized)
        )
      : templates;
    const map = {};
    filtered.forEach(template => (map[template.errorType] = true));
    return errors
      .filter(error => map[error.errorType])
      .map(error => {
        return { ...error, template: templates.find(template => template.errorType === error.errorType) };
      })
      .sort((a, b) => DateTime.fromISO(b.creationDate).toMillis() - DateTime.fromISO(a.creationDate).toMillis());
  }
);

export const getErrorTemplates = createSelector(
  fromCore.getErrorTemplates,
  getErrors,
  getTemplateSearch,
  (templates: Array<ErrorTemplate>, errors: Array<Error>, search: string) => {
    const normalized = StringHelper.normalize(search);
    const filtered = search
      ? templates.filter(
          template =>
            StringHelper.normalize(template.title).includes(normalized) ||
            StringHelper.normalize(template.description).includes(normalized)
        )
      : templates;
    return filtered
      .map(template => {
        const occurences = errors.filter(error => error.errorType === template.errorType);
        return {
          ...template,
          recentOccurences: occurences.length,
          lastOccurence: occurences?.length
            ? occurences.sort(
                (a, b) => DateTime.fromISO(b.creationDate).toMillis() - DateTime.fromISO(a.creationDate).toMillis()
              )[0].creationDate
            : ''
        } as ErrorTemplateCount;
      })
      .sort((a, b) => b.recentOccurences - a.recentOccurences);
  }
);
